import { Button } from '@curtin-dxp/curtin-ui';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Input, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui';
import { isPlurals } from '@/lib/utils';
import { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { ColumnFiltersState } from '@tanstack/react-table';
import { useAwmsStore } from '@/store/AwmsStore';
import { SelectedStaffState, SheetType } from '@/store/types';

interface HdrHeaderProps {
  staffDetails: SelectedStaffState;
  school: string;
  year: string;
  chairAllowance: number;
  supervisorAllowance: number;
  setColumnFilters?: Dispatch<SetStateAction<ColumnFiltersState>>;
}
export const HdrHeader = ({
  staffDetails,
  school,
  year,
  chairAllowance,
  supervisorAllowance,
  setColumnFilters,
}: HdrHeaderProps) => {
  const setSelectedStaff = useAwmsStore((state) => state.setSelectedStaff);
  const setActiveSheet = useAwmsStore((state) => state.setActiveSheet);
  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setColumnFilters?.((prev) => [
      ...prev.filter((f) => f.id !== 'student'),
      ...(value ? [{ id: 'student', value }] : []),
    ]);
  };

  const handleRedirect = (activeSheet: string) => {
    setSelectedStaff({
      id: staffDetails.id,
      firstName: staffDetails.firstName,
      lastName: staffDetails.lastName,
    });
    setActiveSheet(activeSheet as SheetType);
  };

  return (
    <SheetHeader className="m-0 flex w-full flex-col gap-0 space-y-0">
      <div className="m-0 flex h-[112px] w-full flex-row items-center justify-between self-stretch bg-grey-100 px-8 py-[20px] pr-14">
        <div className="mb-0 mr-2 flex items-center gap-10">
          <div className="flex w-[189] shrink-0 flex-col items-start gap-1">
            <SheetTitle className="text-sh3 text-primary-black-90">HDR allocations</SheetTitle>
            <SheetDescription className="text-b2 text-primary-black-90">
              {`${staffDetails?.lastName?.toString() ?? ''}, ${staffDetails?.firstName?.toString() ?? ''}`}
            </SheetDescription>
            <Button
              onClick={() => handleRedirect('staff-workload')}
              className="m-0 w-fit border-none p-0 text-left text-xs font-semibold text-deep-blue underline"
              variant={null}
            >
              Allocate staff workload
            </Button>
          </div>
          <div className="relative flex w-[260px] items-center">
            <Input
              placeholder="Search for a student"
              onChange={handleSearch}
              className="h-10 bg-white px-3 pl-5 pr-9 text-base font-normal leading-6 placeholder:text-grey-600 hover:border-deep-blue
                focus-visible:ring-deep-blue"
              aria-label="Search student"
            />
            <MagnifyingGlassIcon className="absolute right-3 h-[18px] w-[18px]" />
          </div>
        </div>
        <div className="flex shrink items-start justify-center gap-3 text-b3 font-semibold text-secondary-grey-02">
          <span className="ml-1 flex shrink">{school}</span>
          <span className="flex shrink-0 self-center align-middle">{year}</span>
        </div>
      </div>
      <div className="m-0 flex h-[56px] w-full flex-row items-center justify-start gap-[60px] bg-black px-8 py-4">
        <div className="text-b2 font-bold text-white">Base allocation:</div>
        <div className="text-b3 font-semibold text-secondary-grey-04">
          Chairperson{' '}
          <span className="font-normal">
            {chairAllowance} hour{isPlurals(chairAllowance) ? 's' : ''}
          </span>
        </div>
        <div className="text-b3 font-semibold text-secondary-grey-04">
          Supervisor{' '}
          <span className="font-normal">
            {supervisorAllowance} hour{isPlurals(supervisorAllowance) ? 's' : ''}
          </span>
        </div>
      </div>
    </SheetHeader>
  );
};
